import { useState } from 'react'
import { Head, router } from '@inertiajs/react'

import AdminLayout from '../../../components/AdminLayout'

// Light (top) → intense (bottom) ramp built on the shop's accent pink.
const PINK_STOPS = ['#FBD3DD', '#F7A6BB', '#F27B9B', '#EE4266', '#C22B4E', '#8F1D38']
const MIN_WIDTH = 26 // % — keep the narrowest step readable

type FunnelRef = { id: number; name: string; slug: string }
type Step = { key: string; label: string; value: number | null; future?: boolean }

type Props = {
  funnels: FunnelRef[]
  funnel: FunnelRef | null
  filters: { from: string | null; to: string | null }
  adSteps: Step[]
  siteSteps: Step[]
}

// Conversion funnel visualisation (ported from dawn) in the Fresh Picked
// admin language: CSS clip-path polygons over the pink ramp, per-step
// conversion bubbles, a funnel switcher and a date range filter.
export default function AdminFunnelIndex({ funnels, funnel, filters, adSteps, siteSteps }: Props) {
  const [from, setFrom] = useState(filters.from ?? '')
  const [to, setTo] = useState(filters.to ?? '')

  const apply = (overrides: Record<string, string | number | null> = {}) => {
    const query: Record<string, string | number> = {}
    const funnelId = overrides.funnel_id ?? funnel?.id
    if (funnelId) query.funnel_id = funnelId
    const nextFrom = overrides.from !== undefined ? overrides.from : from
    const nextTo = overrides.to !== undefined ? overrides.to : to
    if (nextFrom) query.from = String(nextFrom)
    if (nextTo) query.to = String(nextTo)
    router.get('/admin/funnel', query, { preserveScroll: true })
  }

  const clear = () => {
    setFrom('')
    setTo('')
    apply({ from: null, to: null })
  }

  return (
    <AdminLayout>
      <Head title="Funnel — Sick Stuff Admin" />

      <div className="sss-admin-head">
        <div className="sss-admin-head__row">
          <div>
            <p className="sss-admin-eyebrow sss-admin-eyebrow--accent">Conversion funnel</p>
            <h1 className="sss-admin-title">{funnel?.name ?? 'No funnel'}</h1>
          </div>

          <div className="sss-admin-field">
            <span className="sss-admin-label">Funnel</span>
            <select
              className="sss-admin-select"
              style={{ minWidth: '12rem' }}
              value={funnel?.id ?? ''}
              onChange={(event) => apply({ funnel_id: Number(event.target.value) })}
            >
              {funnels.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Date filter */}
      <div className="sss-admin-filters">
        <div className="sss-admin-field">
          <label htmlFor="from" className="sss-admin-label">
            From
          </label>
          <input
            id="from"
            type="date"
            className="sss-admin-input"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div className="sss-admin-field">
          <label htmlFor="to" className="sss-admin-label">
            To
          </label>
          <input
            id="to"
            type="date"
            className="sss-admin-input"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>
        <button type="button" className="sss-admin-btn sss-admin-btn--sm" onClick={() => apply()}>
          Apply
        </button>
        {(from || to) && (
          <button type="button" className="sss-admin-btn sss-admin-btn--ghost sss-admin-btn--sm" onClick={clear}>
            Clear
          </button>
        )}
        <span className="sss-admin-filters__meta">
          {from || to ? `${from || 'start'} → ${to || 'now'}` : 'All time'}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 56, marginTop: 8 }}>
        <FunnelGroup
          eyebrow="The ad"
          title="What happens on the ad"
          note="From Meta/TikTok — wired via webhook later."
          steps={adSteps}
        />
        <FunnelGroup
          eyebrow="The site"
          title="What happens on the site"
          note="Live from tracked visitors."
          steps={siteSteps}
        />
      </div>
    </AdminLayout>
  )
}

function FunnelGroup({
  eyebrow,
  title,
  note,
  steps,
}: {
  eyebrow: string
  title: string
  note: string
  steps: Step[]
}) {
  const values = steps.map((step) => (typeof step.value === 'number' ? step.value : null))
  const groupMax = Math.max(1, ...values.map((value) => value ?? 0))
  const count = steps.length

  const widthOf = (value: number | null) =>
    value === null ? 100 : Math.max(MIN_WIDTH, (value / groupMax) * 100)

  return (
    <section>
      <div style={{ textAlign: 'center' }}>
        <p className="sss-admin-eyebrow sss-admin-eyebrow--accent">{eyebrow}</p>
        <h2 style={{ margin: '8px 0 0', fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em' }}>
          {title}
        </h2>
        <p className="sss-admin-sub" style={{ marginTop: 4 }}>
          {note}
        </p>
      </div>

      <div style={{ position: 'relative', margin: '32px auto 0', width: '100%', maxWidth: 760 }}>
        {steps.map((step, index) => {
          const value = values[index]
          const topW = widthOf(value)
          const botW =
            index < count - 1 ? widthOf(values[index + 1]) : Math.max(MIN_WIDTH * 0.62, topW * 0.72)

          const lt = (100 - topW) / 2
          const rt = (100 + topW) / 2
          const lb = (100 - botW) / 2
          const rb = (100 + botW) / 2
          const clipPath = `polygon(${lt}% 0, ${rt}% 0, ${rb}% 100%, ${lb}% 100%)`

          const frac = count > 1 ? index / (count - 1) : 1
          const background =
            value === null ? '#E5E4E2' : PINK_STOPS[Math.round(frac * (PINK_STOPS.length - 1))]
          const textColor = value === null ? '#B1B1B4' : frac < 0.45 ? '#500A1C' : '#EEEDEB'

          const previous = index > 0 ? values[index - 1] : null
          const conversion =
            value !== null && typeof previous === 'number' && previous > 0
              ? Math.round((value / previous) * 100)
              : null

          return (
            <div key={step.key} style={{ position: 'relative', height: 74 }}>
              <div
                style={{ position: 'absolute', inset: 0, clipPath, background, opacity: step.future ? 0.5 : 1 }}
              />
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'grid',
                  placeItems: 'center',
                  padding: '0 24px',
                  textAlign: 'center',
                  color: textColor,
                }}
              >
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1, fontFeatureSettings: '"tnum"' }}>
                    {value === null ? '—' : value.toLocaleString()}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13, fontWeight: 500, lineHeight: 1.2, opacity: 0.9 }}>
                    {step.label}
                    {step.future ? ' · soon' : ''}
                  </div>
                </div>
              </div>

              {conversion !== null && (
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: 0,
                    zIndex: 10,
                    transform: 'translate(-50%, -50%)',
                    background: 'var(--sss-bg-alt)',
                    border: '1px solid var(--sss-line)',
                    borderRadius: 999,
                    padding: '3px 8px',
                    fontSize: 11,
                    fontFeatureSettings: '"tnum"',
                    color: 'var(--sss-ink-muted)',
                  }}
                >
                  {conversion}%
                </div>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}
